import React from 'react';
import { Card, WarningModal } from 'components';
import { Button } from 'react-bootstrap';
import { IApiError } from '_helpers/ApiConnector/types';
import { TemplateGroup } from './TemplateGroup';
import { IDropZoneUploadedDocument } from './UploadedDocument';
import { DropZone, IDropZoneProps } from './DropZone';
import styles from './DocumentUpLoader.module.scss';
import cn from 'classnames';

const DocumentUpLoader = ({
    onGoBack,
    onSubmit,
    documentTemplateGroups,
    deleteDocument,
    uploadDocument,
    deleteDocumentSource,
    uploadedDocuments: nUploadedDocuments,
    correctionsRequired,
    apiError,
    goBackButtonText,
    submitButtonText,
    withoutPadding,
    onSelectTemplate,
}: IProps) => {
    const [showWarningModal, setShowWarningModal] = React.useState(false);
    const [documentsPack, setDocumentsPack] = React.useState<ITemplate[][]>([]);
    const [selectedTemplateGroup, setSelectedTemplateGroup] = React.useState<ITemplate[] | null>(null);
    const [selectedTemplateGroupNum, setSelectedTemplateGroupNum] = React.useState<number | null>(null);
    const [uploadedDocuments, setUploadedDocuments] = React.useState<IDropZoneUploadedDocument[]>([]);
    const [loadingFile, setLoadingFile] = React.useState<ILoadingFile | null>(null);
    const [lastDropTemplateId, setLastDropTemplateId] = React.useState<string | null>(null);

    const identificationDocument = documentTemplateGroups.length > 0 ? documentTemplateGroups[0].templates : [];
    const dropZoneDocuments = selectedTemplateGroup ? [...identificationDocument, ...selectedTemplateGroup] : [];

    React.useEffect(() => {
        if (!apiError) {
            setUploadedDocuments(nUploadedDocuments);
        }
        setLoadingFile(null);
    }, [nUploadedDocuments]);

    React.useEffect(() => {
        if (apiError) {
            setLoadingFile(null);
        }
    }, [apiError]);

    React.useEffect(() => {
        if (loadingFile && loadingFile.progress === 0) {
            const timer = setTimeout(() => {
                setLoadingFile({ ...loadingFile, progress: 50 });
            }, 1000);
            return () => clearTimeout(timer);
        }

        return undefined;
    }, [loadingFile]);

    React.useEffect(() => {
        const arr: ITemplate[][] = [];
        documentTemplateGroups.forEach((group) => {
            group.templates.forEach((template) => {
                if (!arr[template.variant]) {
                    arr[template.variant] = [];
                }

                arr[template.variant].push(template);
            });
        });

        setDocumentsPack(arr);
    }, [documentTemplateGroups]);

    React.useEffect(() => {
        if (correctionsRequired && documentsPack.length && uploadedDocuments.length) {
            let num: number | null = null;
            let group: ITemplate[] | null = null;
            const { documentTemplateId } = uploadedDocuments[1];

            if (documentTemplateId) {
                documentsPack.find((temp, i) => {
                    const res = temp.find(e => e.id === documentTemplateId);

                    if (res) {
                        num = i;
                        group = documentsPack[i];
                    }

                    return res;
                });
            }

            if (num !== null && group !== null) {
                setSelectedTemplateGroup(group);
                setSelectedTemplateGroupNum(num);
            }
        } else {
            setSelectedTemplateGroup(null);
            setSelectedTemplateGroupNum(null);
        }
    }, [correctionsRequired]);

    const onDeleteFile = (documentId: string) => {
        deleteDocument(documentId);
    };

    const onUploadFile = (templateId: string, file: File) => {
        uploadDocument(
            templateId,
            file,
        );
    };

    const onDeleteSource = (documentId: string, sourceFileId: string) => {
        deleteDocumentSource(
            documentId,
            sourceFileId,
        );
    };

    const onDrop = (acceptedFiles: File[], templateId: string) => {
        // const formData = new FormData();
        // formData.append('file', acceptedFiles[0]);

        // const xhr = new XMLHttpRequest();
        // xhr.open('POST', '/');
        // xhr.send(formData);
        // xhr.upload.onprogress = (event) => {
        //     const percentages = +((event.loaded / event.total) * 100).toFixed(2);
        //     setUploadProgress(percentages);
        // };
        // xhr.onreadystatechange = () => {
        //     if (xhr.readyState !== 4) return;
        //     if (xhr.status !== 200) {
        //         /* handle error */
        //     }
        //     /* handle success */
        // };
        setLastDropTemplateId(templateId);
        setLoadingFile({ file: acceptedFiles[0], progress: 0 });
        onUploadFile(templateId, acceptedFiles[0]);
    };

    const isUploadedDropZone = (documentTemplateId: string) => uploadedDocuments.length && uploadedDocuments.find(file => file.documentTemplateId === documentTemplateId);

    const title = !selectedTemplateGroup ? 'Выберите пакет документов' : 'Загрузите документы';
    const category = !selectedTemplateGroup
        ? 'Выберите подходящий вам пакет документов для загрузки сканов'
        : `Вариант комплекта документов ${selectedTemplateGroupNum !== null ? (selectedTemplateGroupNum + 1) : ''}`;

    return (
        <Card
          title={title}
          category={category}
          withoutPadding={withoutPadding}
          withoutPaddingTitle={withoutPadding}
          contentClassName={styles.content}
          withoutBorder
        >
            {!selectedTemplateGroup ? (
                <>
                    {documentsPack.map((template, i) => i !== 0 && template && (
                        <TemplateGroup
                          key={`template_${i}`}
                          templateGroup={documentsPack[0].concat(template)}
                          templateNum={i}
                          onSelect={() => {
                                setSelectedTemplateGroup(template);
                                setSelectedTemplateGroupNum(i);
                                if (onSelectTemplate) {
                                    onSelectTemplate(true);
                                }
                            }}
                        />
                    ))}

                    {onGoBack ? (
                        <Button variant="outline-secondary" className={styles.button} onClick={onGoBack}>
                            {goBackButtonText || 'На предыдущий шаг'}
                        </Button>
                    ) : null}
                </>
            ) : (
                    <>
                        {dropZoneDocuments.map((template, i) => (template ? (
                            <DropZone
                              key={`dropZone_${i}`}
                              template={template}
                              templateNum={i + 1}
                              uploadedDocuments={uploadedDocuments}
                              apiError={apiError}
                              onDrop={onDrop}
                              onDeleteFile={onDeleteFile}
                              onDeleteSource={onDeleteSource}
                              loadingFile={loadingFile}
                              lastDropTemplateId={lastDropTemplateId}
                            />
                        ) : null))}
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowWarningModal(true)}
                          className={styles.button}
                        >
                            Выбрать другой пакет документов
                        </Button>
                        {dropZoneDocuments.every(template => template && isUploadedDropZone(template.id)) ? (
                            <Button
                              variant="primary"
                              className={cn('ml-4', 'pl-4', 'pr-4', styles.button)}
                              onClick={onSubmit}
                            >
                                {submitButtonText || 'Следующий шаг'}
                            </Button>
                        ) : null}
                    </>
                )}
            <WarningModal
              title="Подтвердите действие"
              accessBtnTittle="Да, выбрать другой пакет документов"
              closeBtnTittle="Отмена"
              show={showWarningModal}
              onHide={() => setShowWarningModal(false)}
              onAccess={() => {
                    setShowWarningModal(false);
                    setSelectedTemplateGroup(null);
                    if (onSelectTemplate) {
                        onSelectTemplate(false);
                    }
                }}
              withoutTitlePadding
            >
                <div style={{ maxWidth: '450px' }}>
                    <span className="d-block mb-4">В случае выбора другого пакета документов, вся информация по документам, загруженная до этого, будет удалена.</span>
                    <strong>Вы уверены, что хотите выбрать другой пакет документов для загрузки?</strong>
                </div>
            </WarningModal>
        </Card>
    );
};

export default DocumentUpLoader;

export interface IProps {
    documentTemplateGroups: IDocumentTemplateGroup[];
    uploadedDocuments: IDropZoneProps['uploadedDocuments'];
    apiError: IApiError | null | undefined;
    correctionsRequired?: boolean;
    goBackButtonText?: string;
    submitButtonText?: string;
    withoutPadding?: boolean;
    onSubmit: () => void;
    deleteDocument: (documentId: string) => void;
    uploadDocument: (templateId: string, file: File) => void;
    deleteDocumentSource: (documentId: string, sourceFileId: string) => void;
    onSelectTemplate?: (selectTemplate: boolean) => void;
    onGoBack?: () => void;
}

interface IDocumentTemplateGroup {
    templates: ITemplate[];
}

type ITemplate = IDropZoneProps['template'];

interface ILoadingFile {
    file: File;
    progress: number;
}
